import { Injectable } from '@angular/core';
import {
  ApplicationModuleService,
  ApplicationService,
} from '@soleran/application';
import { SearchModuleService, UserHistoryService } from '@soleran/audit';
import {
  IModule,
  IRecordDataset,
  ModuleService,
  RecordService,
} from '@soleran/module';
import { EMPTY, forkJoin, Observable, of } from 'rxjs';
import {
  bufferCount,
  catchError,
  concatMap,
  distinct,
  first,
  map,
  mergeMap,
  shareReplay,
  tap,
  toArray,
} from 'rxjs/operators';
import { IFieldSearchMatch, IRecordSearchResult } from './record-search-result';

@Injectable()
export class GlobalSearchService {
  moduleCache: { [moduleId: number]: IModule } = {};
  constructor(
    private _hist: UserHistoryService,
    private _as: ApplicationService,
    private _ams: ApplicationModuleService,
    private _rs: RecordService,
    private _ms: ModuleService,
    private _ss: SearchModuleService
  ) {}

  get(params: {
    searchText: string;
    moduleIds?: (string | number)[];
    maxResultsPerModule?: number;
    /**
     * Defaults to 10
     */
    maxResults?: number;
  }): Observable<IRecordSearchResult> {
    if (!params.searchText || !params.searchText.length) {
      return EMPTY;
    }
    const links = this._ss.get(params);
    const results$ = this._getRecords(links).pipe(
      mergeMap((dataset: IRecordDataset) =>
        this._getCachedModuleOrFetchAndCache(dataset.moduleId).pipe(
          map((m) => this._mapRecordSearchResult(params.searchText, m, dataset))
        )
      )
    );
    return results$;
  }
  getHistory() {
    return this._hist.get().pipe(shareReplay(1));
  }
  getAppModules() {
    return this._as.get().pipe(
      mergeMap((apps) => apps.map((a) => a.id)),
      mergeMap((appId) => this._ams.get(appId)),
      mergeMap((cfgs) => cfgs.map((c) => c.moduleId)),
      distinct((id) => id),
      mergeMap((id) => this._ms.get(id)),
      toArray(),
      first((v) => !!v, [] as IModule[]),
      shareReplay(1)
    );
  }
  private _mapRecordSearchResult(
    term: string,
    module: IModule,
    dataset: IRecordDataset
  ): IRecordSearchResult {
    const matches: IFieldSearchMatch[] = [];
    const record = dataset.records[0];
    for (let i = 0; i < record.values.length; i++) {
      const value = (record.values[i] + '').toLocaleLowerCase();
      if (value.includes(term)) {
        const fieldId = dataset.columnDefinitions[i];
        const label = dataset.columnLabels[i];
        matches.push({
          fieldId,
          label,
          value,
        });
      }
    }
    const recordLink = {
      moduleId: module.id,
      recordId: dataset.records[0].id,
    };

    return {
      module,
      matches,
      recordLink,
      date:
        record.values[dataset.columnDefinitions.indexOf('updateddate')] ||
        record.values[dataset.columnDefinitions.indexOf('addeddate')],
    };
  }

  private _getRecords(links$: Observable<[number, number][]>) {
    return links$.pipe(
      mergeMap((l) => l),
      bufferCount(3),
      concatMap((items) =>
        forkJoin(items.map((l) => this._getRecordOrDefault(l))).pipe(
          first(null, [])
        )
      ),
      mergeMap((v) => v)
    );
  }
  private _getRecordOrDefault(
    link: [number, number]
  ): Observable<IRecordDataset> {
    return this._rs
      .get(...link)
      .pipe(catchError((e) => of(null))) as Observable<IRecordDataset>;
  }

  private _getCachedModuleOrFetchAndCache(moduleId: number) {
    const cache = this.moduleCache[moduleId];
    return cache
      ? of(cache)
      : this._ms.get(moduleId).pipe(tap((m) => (this.moduleCache[m.id] = m)));
  }
}
